'use client';

/**
 * Service Referrals List Component
 *
 * Displays a list of all service referrals for the organization with:
 * - Filtering by status, urgency, and service type
 * - Search functionality
 * - Quick actions for common operations
 * - Navigation to detailed workspace view
 *
 * Note: Service referrals are employer-specific and require the 'employer_platform'
 * capability to be enabled for the organization.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Search,
  Filter,
  Plus,
  RefreshCw,
  FileText,
  Clock,
  User,
  Building2,
  Calendar,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  ArrowUpDown,
  Lock,
  DollarSign,
  Heart,
} from 'lucide-react';
import {
  serviceReferralsAPI,
  ServiceReferral,
  ReferralStatus,
  getReferralStatusLabel,
  getReferralStatusColor,
  getUrgencyLabel,
  getUrgencyColor,
} from '@/services/api/serviceReferrals';
import { useCapability } from '@/hooks/useCapabilities';
import { PricingContextPanel } from './PricingContextPanel';
import { useToast } from '@/components/ui/use-toast';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ServiceReferralsListProps {
  onSelectReferral?: (referralId: number) => void;
  onCreateNew?: () => void;
  className?: string;
  /** Optional employer ID to filter referrals by employer */
  employerId?: number;
  /** Organization ID for capability checking - defaults to active_cbo_id from localStorage */
  organizationId?: number;
}

// Status filter options
const STATUS_FILTERS: { value: ReferralStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'intake_scheduled', label: 'Intake Scheduled' },
  { value: 'intake_completed', label: 'Intake Completed' },
  { value: 'services_in_progress', label: 'In Progress' },
  { value: 'services_completed', label: 'Completed' },
  { value: 'closed_successful', label: 'Closed (Successful)' },
  { value: 'closed_unsuccessful', label: 'Closed (Unsuccessful)' },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function ServiceReferralsList({
  onSelectReferral,
  onCreateNew,
  className = '',
  employerId,
  organizationId,
}: ServiceReferralsListProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  // Get organization ID from props or localStorage
  const effectiveOrgId = organizationId || parseInt(localStorage.getItem('active_cbo_id') || '0', 10);

  // Check if employer_platform capability is enabled
  const { enabled: hasEmployerPlatform, loading: capabilityLoading } = useCapability(
    effectiveOrgId,
    'employer_platform'
  );

  const { toast } = useToast();

  // State
  const [referrals, setReferrals] = useState<ServiceReferral[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReferralStatus | 'all'>('all');
  const [sortBy, setSortBy] = useState<'created_at' | 'updated_at' | 'urgency'>('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Create referral dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createDialogTab, setCreateDialogTab] = useState<'service' | 'pricing'>('service');
  const [serviceTypes, setServiceTypes] = useState<{ code: string; name: string }[]>([]);
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [referralUrgency, setReferralUrgency] = useState<'routine' | 'urgent' | 'emergency'>('routine');
  const [referralDescription, setReferralDescription] = useState('');
  const [triggerMatching, setTriggerMatching] = useState(true);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [selectedMemberName, setSelectedMemberName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Load service types for create dialog
  useEffect(() => {
    const loadServiceTypes = async () => {
      try {
        const types = await serviceReferralsAPI.getServiceTypes();
        setServiceTypes(types);
      } catch (err) {
        console.error('Failed to load service types:', err);
      }
    };
    loadServiceTypes();
  }, []);

  // Load referrals
  const loadReferrals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await serviceReferralsAPI.getReferrals({
        employer_id: employerId,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchQuery || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setReferrals(data);
    } catch (err) {
      console.error('Failed to load service referrals:', err);
      setError('Failed to load service referrals. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [employerId, statusFilter, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    loadReferrals();
  }, [loadReferrals]);

  // Format date for display
  const formatDate = (dateStr: string) => {
    return fmt.medium(dateStr);
  };

  // Toggle sort order
  const toggleSort = (field: 'created_at' | 'updated_at' | 'urgency') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Get status icon
  const getStatusIcon = (status: ReferralStatus) => {
    if (status.includes('completed') || status.includes('successful')) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    if (status.includes('unsuccessful') || status.includes('cancelled') || status.includes('rejected')) {
      return <XCircle className="w-4 h-4 text-red-500" />;
    }
    if (status.includes('pending') || status.includes('scheduled')) {
      return <Clock className="w-4 h-4 text-yellow-500" />;
    }
    return <AlertCircle className="w-4 h-4 text-blue-500" />;
  };

  // Calculate stats
  const stats = {
    total: referrals.length,
    inProgress: referrals.filter(r => r.status === 'services_in_progress').length,
    pendingReview: referrals.filter(r => r.status === 'pending_review').length,
    completed: referrals.filter(r => r.status.includes('completed') || r.status.includes('successful')).length,
  };

  // Handle create referral
  const handleCreateReferral = async () => {
    if (!selectedServiceType) {
      toast({
        title: 'Error',
        description: 'Please select a service type',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const result = await serviceReferralsAPI.createManualReferral({
        employee_id: selectedMemberId || undefined,
        service_type_code: selectedServiceType,
        urgency: referralUrgency,
        service_description: referralDescription || undefined,
        trigger_matching: triggerMatching,
      });

      toast({
        title: 'Referral Created',
        description: `${result.message}. ${result.matched_providers_count || 0} providers matched.`,
      });

      // Reset form and close dialog
      setShowCreateDialog(false);
      setSelectedServiceType('');
      setReferralUrgency('routine');
      setReferralDescription('');
      setTriggerMatching(true);
      setSelectedMemberId(null);
      setSelectedMemberName('');
      setCreateDialogTab('service');

      // Reload referrals
      await loadReferrals();

      // If we have the new referral ID, select it
      if (result.referral_id && onSelectReferral) {
        onSelectReferral(result.referral_id);
      }
    } catch (err: any) {
      console.error('Error creating referral:', err);
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to create referral',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle opening the create dialog
  const handleOpenCreateDialog = () => {
    setShowCreateDialog(true);
    setCreateDialogTab('service');
  };

  // ========================================================================
  // RENDER
  // ========================================================================

  // Show loading state while checking capability
  if (capabilityLoading) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-600">{t('serviceReferrals.list.checkingAccess', 'Checking access...')}</span>
      </div>
    );
  }

  // Show access denied if employer_platform capability is not enabled
  if (!hasEmployerPlatform) {
    return (
      <div className={`${className}`}>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="p-4 bg-amber-100 rounded-full mb-4">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('serviceReferrals.list.employerPlatformRequired', 'Employer Platform Required')}
            </h2>
            <p className="text-gray-600 max-w-md mb-4">
              {t('serviceReferrals.list.employerPlatformDesc', 'Service referrals are only available for employer organizations. This feature requires the \'employer_platform\' capability to be enabled.')}
            </p>
            <p className="text-sm text-gray-500">
              {t('serviceReferrals.list.contactAdmin', 'If you believe this is an error, please contact your administrator.')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={loadReferrals} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          {t('common.refresh', 'Refresh')}
        </Button>
        <Button size="sm" onClick={handleOpenCreateDialog}>
          <Plus className="w-4 h-4 mr-1" />
          {t('serviceReferrals.list.newReferral', 'New Referral')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</div>
            <div className="text-sm text-gray-500">{t('serviceReferrals.list.totalReferrals', 'Total Referrals')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.pendingReview}</div>
            <div className="text-sm text-gray-500">{t('serviceReferrals.list.pendingReview', 'Pending Review')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.inProgress}</div>
            <div className="text-sm text-gray-500">{t('serviceReferrals.list.inProgress', 'In Progress')}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
            <div className="text-sm text-gray-500">{t('serviceReferrals.list.completed', 'Completed')}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={t('serviceReferrals.list.searchPlaceholder', 'Search by member name, service type...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ReferralStatus | 'all')}
              className="px-3 py-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleSort('updated_at')}
                className={sortBy === 'updated_at' ? 'border-blue-500' : ''}
              >
                <ArrowUpDown className="w-4 h-4 mr-1" />
                {t('serviceReferrals.list.updated', 'Updated')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleSort('urgency')}
                className={sortBy === 'urgency' ? 'border-blue-500' : ''}
              >
                <ArrowUpDown className="w-4 h-4 mr-1" />
                {t('serviceReferrals.list.urgency', 'Urgency')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={loadReferrals} className="ml-auto">
                {t('common.retry', 'Retry')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && referrals.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{t('serviceReferrals.list.noReferralsFound', 'No Service Referrals Found')}</h3>
            <p className="text-gray-500 mb-4">
              {searchQuery || statusFilter !== 'all'
                ? t('serviceReferrals.list.tryAdjustingFilters', 'Try adjusting your search or filter criteria.')
                : t('serviceReferrals.list.getStarted', 'Get started by creating your first service referral.')}
            </p>
            <Button onClick={handleOpenCreateDialog}>
              <Plus className="w-4 h-4 mr-1" />
              {t('serviceReferrals.list.createNewReferral', 'Create New Referral')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Referrals List */}
      {!loading && !error && referrals.length > 0 && (
        <div className="space-y-3">
          {referrals.map((referral) => {
            const statusColor = getReferralStatusColor(referral.status);
            const urgencyColor = getUrgencyColor(referral.urgency_level);

            return (
              <Card
                key={referral.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => onSelectReferral?.(referral.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Header Row */}
                      <div className="flex items-center gap-3 mb-2">
                        {getStatusIcon(referral.status)}
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {referral.member_name || `Member #${referral.member_id}`}
                        </span>
                        <Badge
                          variant="outline"
                          className={`${statusColor.bg} ${statusColor.text} ${statusColor.border}`}
                        >
                          {getReferralStatusLabel(referral.status)}
                        </Badge>
                        <Badge
                          variant="outline"
                          className={`${urgencyColor.bg} ${urgencyColor.text} ${urgencyColor.border}`}
                        >
                          {getUrgencyLabel(referral.urgency_level)}
                        </Badge>
                      </div>

                      {/* Details Row */}
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        {referral.service_type_name && (
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            {referral.service_type_name}
                          </span>
                        )}
                        {referral.referring_organization_name && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {referral.referring_organization_name}
                          </span>
                        )}
                        {referral.assigned_staff_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {referral.assigned_staff_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(referral.created_at)}
                        </span>
                      </div>

                      {/* Progress Info */}
                      {referral.estimated_units && (
                        <div className="mt-2 text-sm">
                          <span className="text-gray-500">{t('serviceReferrals.list.progress', 'Progress')}: </span>
                          <span className="font-medium">
                            {referral.actual_units || 0} / {referral.estimated_units} {t('serviceReferrals.list.units', 'units')}
                          </span>
                          <span className="text-gray-400 ml-2">
                            ({Math.round(((referral.actual_units || 0) / referral.estimated_units) * 100)}%)
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Action Arrow */}
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Referral Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t('serviceReferrals.list.createServiceReferral', 'Create Service Referral')}
            </DialogTitle>
            <DialogDescription>
              {t('serviceReferrals.list.createReferralDesc', 'Create a new service referral with pricing context and provider matching.')}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={createDialogTab} onValueChange={(v) => setCreateDialogTab(v as 'service' | 'pricing')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="service" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t('serviceReferrals.list.serviceDetails', 'Service Details')}
              </TabsTrigger>
              <TabsTrigger value="pricing" className="flex items-center gap-2" disabled={!selectedServiceType}>
                <DollarSign className="h-4 w-4" />
                {t('serviceReferrals.list.pricingContext', 'Pricing Context')}
              </TabsTrigger>
            </TabsList>

            {/* Service Details Tab */}
            <TabsContent value="service" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t('serviceReferrals.list.serviceType', 'Service Type')} *</Label>
                <Select value={selectedServiceType} onValueChange={setSelectedServiceType}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('serviceReferrals.list.selectServiceType', 'Select service type...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map((st) => (
                      <SelectItem key={st.code} value={st.code}>
                        {st.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('serviceReferrals.list.urgencyLabel', 'Urgency')}</Label>
                  <Select value={referralUrgency} onValueChange={(v: any) => setReferralUrgency(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="routine">{t('serviceReferrals.list.routine', 'Routine')}</SelectItem>
                      <SelectItem value="urgent">{t('serviceReferrals.list.urgent', 'Urgent')}</SelectItem>
                      <SelectItem value="emergency">{t('serviceReferrals.list.emergency', 'Emergency')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-2">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="trigger-matching"
                      checked={triggerMatching}
                      onCheckedChange={setTriggerMatching}
                    />
                    <Label htmlFor="trigger-matching" className="text-sm">{t('serviceReferrals.list.autoMatchProviders', 'Auto-match providers')}</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('serviceReferrals.list.referralDescription', 'Referral Description')}</Label>
                <Textarea
                  placeholder={t('serviceReferrals.list.describeReferral', 'Describe the referral reason and any specific needs...')}
                  value={referralDescription}
                  onChange={(e) => setReferralDescription(e.target.value)}
                  rows={3}
                />
              </div>

              {selectedServiceType && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-800 text-sm">
                    <DollarSign className="h-4 w-4" />
                    <span>{t('serviceReferrals.list.clickPricingTab', 'Click the "Pricing Context" tab to review rates and ML recommendations')}</span>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Pricing Context Tab */}
            <TabsContent value="pricing" className="space-y-4 mt-4">
              {selectedServiceType ? (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600">
                    {t('serviceReferrals.list.reviewPricingFor', 'Review the pricing information for')} <span className="font-medium">{serviceTypes.find(st => st.code === selectedServiceType)?.name || selectedServiceType}</span>
                  </div>
                  <PricingContextPanel
                    serviceTypeCode={selectedServiceType}
                    urgencyLevel={referralUrgency === 'emergency' ? 'emergent' : referralUrgency}
                    showComparatives={true}
                    showMLRecommendation={true}
                    compact={false}
                  />
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <DollarSign className="h-4 w-4 text-blue-600 mt-0.5" />
                      <div className="text-sm text-blue-900">
                        <p className="font-medium">{t('serviceReferrals.list.pricingSetAutomatically', 'Pricing is set automatically')}</p>
                        <p className="text-blue-700 mt-1">
                          {t('serviceReferrals.list.pricingAutoDesc', 'The pricing shown above will be used as the initial proposal when matching with providers. Providers can accept, counter, or negotiate the rate during the acceptance process.')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <DollarSign className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>{t('serviceReferrals.list.selectServiceTypeFirst', 'Select a service type first to see pricing information')}</p>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleCreateReferral} disabled={!selectedServiceType || submitting}>
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              {t('serviceReferrals.list.createReferralBtn', 'Create Referral')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ServiceReferralsList;
